import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Main Colors */
    --primary-gradient: linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%);
    --secondary-gradient: linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%);
    
    /* Accent Colors - Orange */
    --accent-orange: #ff9600;
    --accent-orange-light: #ffb340;
    --accent-orange-dark: #cc7700;
    
    /* Text Colors - Gray */
    --text-primary: #363636;
    --text-secondary: #363636;
    --text-muted: #6B6B6B;
    --text-light: #FFFFFF;
    --text-dark: #363636;
    --text-gray: #363636;
    
    /* Background Colors */
    --bg-primary: #FFFFFF;
    --bg-secondary: #F5F5F5;
    --bg-tertiary: #E8E8E8;
    --bg-sidebar: #F5F5F5;
    --bg-overlay: rgba(255, 255, 255, 0.95);
    
    /* Card Colors */
    --card-bg: #FFFFFF;
    --card-border: #E0E0E0;
    --card-hover: #FAFAFA;
    --card-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    
    /* Button Colors */
    --button-orange: #ff9600;
    --button-orange-hover: #cc7700;
    --button-light: linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%);
    --button-light-hover: linear-gradient(135deg, #F1F3F5 0%, #E9ECEF 100%);
    --button-danger: linear-gradient(135deg, #FF3333 0%, #CC0000 100%);
    --button-danger-hover: linear-gradient(135deg, #CC0000 0%, #990000 100%);
    
    /* Shadows */
    --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 8px rgba(0, 0, 0, 0.08);
    --shadow-lg: 0 8px 16px rgba(0, 0, 0, 0.1);
    --shadow-hover: 0 12px 24px rgba(0, 0, 0, 0.12);
    
    /* Border Radius */
    --radius-sm: 8px;
    --radius-md: 12px;
    --radius-lg: 16px;
    --radius-full: 9999px;
    
    /* Spacing */
    --spacing-xs: 0.25rem;
    --spacing-sm: 0.5rem;
    --spacing-md: 1rem;
    --spacing-lg: 1.5rem;
    --spacing-xl: 2rem;
    
    /* Transitions */
    --transition-fast: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    --transition-normal: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    /* Rectangle button text - keep single line, compact */
    --button-font-size: 0.6rem;
    --button-padding: 0.4rem 0.75rem;

    /* Mobile typography (max-width: 768px) - consistent across app */
    --mobile-paragraph: 0.75rem;
    --mobile-list-cell: 0.65rem;
    --mobile-list-header: 0.6rem;
    --mobile-headline: 1.5rem;
    --mobile-section-title: 1rem;
    --mobile-tab-font: 0.6rem;
    --mobile-tab-padding: 0.3rem 0.45rem;
    --mobile-legend-font: 0.5rem;
    --mobile-legend-icon: 8;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Poppins', 'Inter', sans-serif;
    background: var(--bg-primary);
    color: var(--text-primary);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Poppins', 'Inter', sans-serif;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: var(--spacing-md);
    color: var(--text-primary);
  }

  h1 { 
    font-size: 2.75rem;
    color: var(--accent-orange);
  }
  h2 { 
    font-size: 2.25rem;
    color: var(--text-dark);
  }
  h3 { 
    font-size: 1.75rem;
    color: var(--accent-orange);
  }
  h4 { 
    font-size: 1.5rem;
    color: var(--text-dark);
  }
  h5 { 
    font-size: 1.25rem;
    color: var(--accent-orange);
  }
  h6 { 
    font-size: 1rem;
    color: var(--text-dark);
  }

  p {
    margin-bottom: var(--spacing-md);
    color: var(--text-secondary);
    font-size: 1.1rem;
    line-height: 1.6;
    font-family: 'Poppins', 'Inter', sans-serif;
  }

  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background: var(--bg-secondary);
    border-radius: var(--radius-full);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--accent-orange);
    border-radius: var(--radius-full);
    border: 2px solid var(--bg-secondary);
    transition: var(--transition-normal);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--accent-orange-dark);
  }

  /* Selection Styling */
  ::selection {
    background: var(--accent-orange);
    color: var(--text-light);
  }

  /* Focus Styles */
  :focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.3);
  }

  /* Links */
  a {
    color: var(--accent-orange);
    text-decoration: none;
    transition: var(--transition-normal);
    font-weight: 500;
    font-family: 'Poppins', 'Inter', sans-serif;

    &:hover {
      color: var(--accent-orange-dark);
      text-decoration: underline;
    }
  }
`;

export default GlobalStyles; 